if (!customElements.get('koko-balm-reviews')) {
  customElements.define('koko-balm-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.pageSize = Number(this.dataset.pageSize) || 2;
      this.limit = this.pageSize;
      this.list = this.querySelector('[data-review-list]');
      this.template = this.querySelector('[data-review-template]');
      this.filters = [...this.querySelectorAll('[data-filter-group]')];
      this.selectedFilters ||= Object.fromEntries(this.filters.map(group => [group.dataset.filterGroup, new Set()]));
      this.sort = this.querySelector('[data-sort]');
      const options = { signal: this.events.signal };
      this.querySelector('[data-filter-toggle]').addEventListener('click', event => {
        const button = event.currentTarget;
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(open));
        this.querySelector('[data-filter-panel]').hidden = !open;
      }, options);
      this.addEventListener('change', event => {
        if (event.target.matches('[data-sort]')) {
          this.limit = this.pageSize;
          this.render();
        }
      }, options);
      this.querySelector('[data-filter-panel]').addEventListener('click', event => {
        const button = event.target.closest('[data-filter-value], [data-clear-group]');
        if (!button) return;
        const group = button.closest('[data-filter-group]');
        const selected = this.selectedFilters[group.dataset.filterGroup];
        if (button.hasAttribute('data-clear-group')) selected.clear();
        else {
          const value = button.dataset.filterValue;
          if (selected.has(value)) selected.delete(value);
          else selected.add(value);
        }
        this.limit = this.pageSize;
        this.render();
        if (button.hasAttribute('data-clear-group')) group.querySelector('[data-filter-value]')?.focus({ preventScroll: true });
      }, options);
      this.querySelectorAll('[data-clear]').forEach(button => button.addEventListener('click', () => {
        Object.values(this.selectedFilters).forEach(selected => selected.clear());
        this.limit = this.pageSize;
        this.render();
        this.querySelector('[data-filter-toggle]').focus({ preventScroll: true });
      }, options));
      this.querySelector('[data-more]').addEventListener('click', () => {
        const previousCount = this.list.children.length;
        this.limit += this.pageSize;
        this.render(true);
        this.list.children[previousCount]?.querySelector('h3').focus({ preventScroll: true });
      }, options);
      this.querySelector('[data-retry]').addEventListener('click', () => this.load(), options);
      this.list.addEventListener('click', event => {
        const button = event.target.closest('[data-vote]');
        if (!button) return;
        const article = button.closest('[data-review-id]');
        const vote = this.getVote(article.dataset.reviewId) === button.dataset.vote ? '' : button.dataset.vote;
        try { localStorage.setItem(this.voteKey(article.dataset.reviewId), vote); } catch (_) { /* Storage may be disabled. */ }
        this.votes ||= {};
        this.votes[article.dataset.reviewId] = vote;
        this.updateVotes(article);
      }, options);
      if (this.reviews) this.render();
      else {
        this.observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        }, { rootMargin: '600px' });
        this.observer.observe(this);
      }
    }
    async load() {
      if (this.loading) return;
      this.loading = true;
      this.setAttribute('aria-busy', 'true');
      this.querySelector('[data-error]').hidden = true;
      this.querySelector('[data-status]').textContent = 'Loading reviews.';
      try {
        const response = await fetch(this.dataset.source, { signal: this.events.signal });
        if (!response.ok) throw new Error('Review data request failed');
        const data = await response.json();
        if (!Array.isArray(data.reviews)) throw new Error('Invalid review data');
        this.reviews = data.reviews.filter(review => review.id && review.author && review.body && review.rating >= 1 && review.rating <= 5);
        for (const group of this.filters) {
          const key = group.dataset.filterGroup;
          if (key === 'rating') continue;
          let values = [...new Set(this.reviews.flatMap(review => this.filterValues(review, key)))];
          if (key === 'ageRange') values = [...new Set(['18 - 24', ...values])].filter(value => /^\d/.test(value) && parseInt(value, 10) >= 18).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
          const choices = values.map(value => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'koko-balm-reviews__choice';
            button.dataset.filterValue = value;
            button.setAttribute('aria-pressed', 'false');
            button.textContent = value;
            return button;
          });
          group.querySelector('[data-filter-options]').replaceChildren(...choices);
        }
        const average = this.reviews.length ? this.reviews.reduce((sum, review) => sum + review.rating, 0) / this.reviews.length : 0;
        const label = average.toFixed(1);
        this.querySelector('[data-average]').textContent = label;
        this.querySelector('[data-rating-label]').setAttribute('aria-label', `${label} out of 5 stars`);
        this.querySelector('[data-rating-label] .koko-balm-reviews__stars').style.setProperty('--rating', `${Number(label) * 20}%`);
        this.querySelector('[data-total]').textContent = this.reviews.length ? `Based on ${this.reviews.length.toLocaleString('en')} reviews` : 'No reviews yet.';
        this.querySelector('[data-rating-label]').hidden = !this.reviews.length;
        this.querySelector('.koko-balm-reviews__header h2').textContent = this.reviews.length ? 'Average rating' : 'Customer reviews';
        this.querySelector('[data-controls]').hidden = !this.reviews.length;
        this.render();
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.querySelector('[data-error]').hidden = false;
          this.querySelector('[data-total]').textContent = 'Customer reviews';
          this.querySelector('[data-status]').textContent = 'Reviews could not be loaded. Please try again.';
        }
      } finally {
        this.loading = false;
        this.removeAttribute('aria-busy');
      }
    }
    filterValues(review, key) {
      const value = review[key];
      if (value == null) return [];
      if (Array.isArray(value)) return value.map(String);
      if (key === 'features' || key === 'concerns') return String(value).split(',').map(item => item.trim()).filter(Boolean);
      return [String(value)];
    }
    render(append = false) {
      if (!this.reviews) return;
      const filtered = this.reviews.filter(review => Object.entries(this.selectedFilters).every(([key, selected]) => !selected.size || this.filterValues(review, key).some(value => selected.has(value))));
      const dateOrder = (a, b) => new Date(b.date) - new Date(a.date);
      filtered.sort((a, b) => {
        if (this.sort.value === 'highest') return b.rating - a.rating || dateOrder(a, b);
        if (this.sort.value === 'lowest') return a.rating - b.rating || dateOrder(a, b);
        if (this.sort.value === 'oldest') return -dateOrder(a, b);
        return dateOrder(a, b);
      });
      const start = append ? this.list.children.length : 0;
      if (!append) this.list.replaceChildren();
      const fragment = document.createDocumentFragment();
      filtered.slice(start, this.limit).forEach(review => fragment.append(this.makeReview(review)));
      this.list.append(fragment);
      this.querySelector('[data-more]').hidden = filtered.length <= this.limit;
      this.querySelector('[data-empty]').hidden = !this.reviews.length || filtered.length !== 0;
      const filterCount = Object.values(this.selectedFilters).reduce((count, selected) => count + selected.size, 0);
      this.filters.forEach(group => {
        const selected = this.selectedFilters[group.dataset.filterGroup];
        group.querySelectorAll('[data-filter-value]').forEach(button => button.setAttribute('aria-pressed', String(selected.has(button.dataset.filterValue))));
        group.querySelector('[data-clear-group]').hidden = !selected.size;
      });
      this.querySelector('.koko-balm-reviews__clear-all').hidden = !filterCount;
      this.querySelector('[data-active-filters]').textContent = filterCount ? `(${filterCount})` : '';
      this.querySelector('[data-status]').textContent = `Showing ${Math.min(this.limit, filtered.length)} of ${filtered.length} reviews.`;
    }
    makeReview(review) {
      const article = this.template.content.firstElementChild.cloneNode(true);
      article.dataset.reviewId = review.id;
      const fields = { author: review.author, title: review.title, body: review.body, age: review.ageRange, concerns: review.concerns, skin: review.skinType, features: review.features };
      for (const [field, value] of Object.entries(fields)) article.querySelector(`[data-${field}]`).textContent = value || '';
      article.querySelector('h3').tabIndex = -1;
      const stars = article.querySelector('[data-review-rating]');
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      stars.querySelector('.koko-balm-reviews__stars').style.setProperty('--rating', `${review.rating * 20}%`);
      article.querySelector('[data-verified]').hidden = !review.verified;
      const date = article.querySelector('[data-date]');
      date.dateTime = review.date;
      date.textContent = this.relativeDate(review.date);
      date.title = new Date(review.date).toLocaleDateString('en', { dateStyle: 'long' });
      const smoothness = Math.max(1, Math.min(5, Number(review.smoothness) || 1));
      const scale = article.querySelector('[data-smoothness]');
      scale.setAttribute('aria-valuenow', String(smoothness));
      scale.style.setProperty('--smoothness', `${(smoothness - 1) * 25}%`);
      this.updateVotes(article);
      return article;
    }
    relativeDate(value) {
      const days = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 86400000));
      if (!days) return 'Today';
      const [count, unit] = days >= 365 ? [Math.floor(days / 365), 'year'] : days >= 30 ? [Math.floor(days / 30), 'month'] : days >= 7 ? [Math.floor(days / 7), 'week'] : [days, 'day'];
      return new Intl.RelativeTimeFormat('en', { numeric: 'always' }).format(-count, unit);
    }
    voteKey(id) { return `koko-balm-review-vote:${id}`; }
    getVote(id) {
      if (this.votes && id in this.votes) return this.votes[id];
      try { return localStorage.getItem(this.voteKey(id)) || ''; } catch (_) { return ''; }
    }
    updateVotes(article) {
      const vote = this.getVote(article.dataset.reviewId);
      article.querySelectorAll('[data-vote]').forEach(button => {
        const selected = vote === button.dataset.vote;
        button.setAttribute('aria-pressed', String(selected));
        button.querySelector('[data-count]').textContent = selected ? '1' : '0';
      });
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.events?.abort();
      this.events = null;
    }
  });
}
